import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  ShieldCheck,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Mail,
  FileText,
  ExternalLink,
} from "lucide-react";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { usePageTracking } from "../hooks/usePageTracking";
import { track } from "../lib/tracking";
import {
  classifySuspension,
  buildEvidenceChecklist,
  TIER_INFO,
  CATEGORIES,
} from "../lib/triageClassifier";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLATFORMS = [
  { id: "instagram", label: "Instagram" },
  { id: "facebook", label: "Facebook" },
  { id: "tiktok", label: "TikTok" },
  { id: "youtube", label: "YouTube" },
  { id: "x", label: "X / Twitter" },
];

const SUSPENSION_TYPES = [
  { id: "disabled", label: "Disabled (you can't log in)" },
  { id: "suspended", label: "Suspended (temporary)" },
  { id: "restricted", label: "Restricted (some features limited)" },
  { id: "banned", label: "Banned / permanent" },
  { id: "hacked", label: "Hacked / compromised" },
  { id: "unsure", label: "Not sure" },
];

const ACCOUNT_TYPES = [
  { id: "personal", label: "Personal account" },
  { id: "creator", label: "Creator / influencer account" },
  { id: "business", label: "Small business / brand" },
  { id: "ad_account", label: "Ad account" },
  { id: "business_manager", label: "Business Manager / agency portfolio" },
];

const SEVERITY_STYLES = {
  critical: "border-red-500/40 bg-red-500/10 text-red-300",
  high: "border-orange-500/40 bg-orange-500/10 text-orange-300",
  "medium-high": "border-yellow-500/40 bg-yellow-500/10 text-yellow-300",
  medium: "border-blue-500/40 bg-blue-500/10 text-blue-300",
  "low-medium": "border-cyan-500/40 bg-cyan-500/10 text-cyan-300",
  unknown: "border-slate-500/40 bg-slate-500/10 text-slate-300",
};

const SEVERITY_LABELS = {
  critical: "Critical severity",
  high: "High severity",
  "medium-high": "Medium-high severity",
  medium: "Medium severity",
  "low-medium": "Low-to-medium severity",
  unknown: "Severity not determined",
};

const TOTAL_STEPS = 4;

export default function SuspensionAnalyzerPage() {
  usePageTracking("Suspension Analyzer", { category: "triage" });

  const [step, setStep] = useState(0);
  const [platform, setPlatform] = useState("");
  const [suspensionType, setSuspensionType] = useState("");
  const [accountType, setAccountType] = useState("");
  const [noticeText, setNoticeText] = useState("");
  const [daysSince, setDaysSince] = useState("");
  const [priorAppeals, setPriorAppeals] = useState(null);
  const [revenueImpacted, setRevenueImpacted] = useState(null);

  // Result + email-capture state
  const [result, setResult] = useState(null);
  const [email, setEmail] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");

  // SEO meta
  useEffect(() => {
    document.title = "Free Suspension Analyzer — Diagnose Your Account Ban | Social Dispute Co";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Free diagnostic tool. Paste your Instagram, Facebook, TikTok, YouTube, or X suspension notice and get a likely category, evidence checklist, and recommended next step — based on each platform's published policies."
      );
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", "https://socialdisputeco.com/analyze");
  }, []);

  const canAdvance = useMemo(() => {
    if (step === 0) return !!platform;
    if (step === 1) return !!suspensionType;
    if (step === 2) return !!accountType;
    if (step === 3) return true; // notice text is optional
    return true;
  }, [step, platform, suspensionType, accountType]);

  const runAnalysis = () => {
    const category = classifySuspension({ noticeText, suspensionType, accountType });
    const checklist = buildEvidenceChecklist({ category, accountType, platform });
    const tier = TIER_INFO[category.recommendedTier] || TIER_INFO.case_review;
    const payload = {
      category,
      checklist,
      tier,
    };
    setResult(payload);
    track("TriageCompleted", {
      platform,
      suspensionType,
      accountType,
      category: category.id,
      severity: category.severity,
      tier: category.recommendedTier,
    });
    setStep(TOTAL_STEPS);
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailSubmitting(true);
    setEmailError("");
    try {
      await axios.post(`${API}/triage`, {
        platform,
        suspensionType,
        accountType,
        suspensionNoticeText: noticeText || null,
        daysSince: daysSince || null,
        priorAppealsSubmitted: priorAppeals,
        revenueImpacted,
        classifiedCategory: result.category.id,
        classifiedSeverity: result.category.severity,
        recommendedTier: result.category.recommendedTier,
        email,
      });
      setEmailSubmitted(true);
      track("TriageLeadCaptured", { tier: result.category.recommendedTier });
    } catch (err) {
      setEmailError("Couldn't save right now. Please try again in a moment.");
    } finally {
      setEmailSubmitting(false);
    }
  };

  // -----------------------------------------------------------------
  // Render helpers
  // -----------------------------------------------------------------
  const renderProgress = () => (
    <div className="flex items-center gap-2 mb-8">
      {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
        <div
          key={`step-pill-${i}`}
          className={`h-1.5 flex-1 rounded-full transition-colors ${
            i <= step ? "bg-blue-500" : "bg-white/10"
          }`}
        />
      ))}
    </div>
  );

  const renderChoice = (options, value, setValue, testidPrefix) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {options.map((opt) => {
        const selected = value === opt.id;
        return (
          <button
            type="button"
            key={opt.id}
            onClick={() => setValue(opt.id)}
            className={`text-left p-4 rounded-xl border transition ${
              selected
                ? "border-blue-500 bg-blue-500/10"
                : "border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/25"
            }`}
            data-testid={`${testidPrefix}-${opt.id}`}
          >
            <span className="text-sm font-medium text-white">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );

  // -----------------------------------------------------------------
  // Result view
  // -----------------------------------------------------------------
  if (step === TOTAL_STEPS && result) {
    const { category, checklist, tier } = result;
    const severityStyle = SEVERITY_STYLES[category.severity] || SEVERITY_STYLES.unknown;
    const severityLabel = SEVERITY_LABELS[category.severity] || category.severity;

    return (
      <div className="min-h-screen bg-slate-950 text-white">
        <Header />
        <main className="py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-6">
            {/* Result header */}
            <div className="mb-10" data-testid="analyzer-result">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/15 border border-blue-500/30 text-blue-300 text-xs font-medium mb-4">
                <Sparkles className="w-3.5 h-3.5" />
                Diagnostic complete
              </div>
              <h1 className="text-3xl md:text-4xl font-bold leading-tight">
                Likely category:
                <span className="block text-blue-400">{category.label}</span>
              </h1>
              <div className={`mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium ${severityStyle}`}>
                <AlertTriangle className="w-3.5 h-3.5" />
                {severityLabel}
              </div>
              <p className="mt-6 text-slate-300 leading-relaxed">
                {category.summary}
              </p>
              <p className="mt-4 text-sm text-slate-400 italic">
                <strong className="text-slate-300">Note:</strong> This is a pattern-matched best
                guess based on the language in your notice and what each platform publishes about its
                enforcement categories. It is not a definitive diagnosis.
              </p>
            </div>

            {/* Appeal outlook */}
            <section className="mb-10 rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8" data-testid="analyzer-outlook">
              <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-blue-400" />
                Appeal outlook
              </h2>
              <p className="text-slate-300 leading-relaxed">{category.appealOutlook}</p>
              <p className="mt-3 text-xs text-slate-500">
                Outcomes are determined entirely by each platform's review team. We never guarantee reinstatement.
              </p>
            </section>

            {/* Recommended next step */}
            <section className="mb-10 rounded-2xl border border-blue-500/30 bg-blue-500/5 p-6 md:p-8" data-testid="analyzer-recommendation">
              <p className="text-xs uppercase tracking-widest text-blue-300 font-semibold mb-2">
                Our recommended path for this category
              </p>
              <div className="flex items-baseline gap-3 flex-wrap">
                <h2 className="text-2xl font-bold">{tier.label}</h2>
                <span className="text-blue-300 font-semibold">{tier.price}</span>
              </div>
              <p className="mt-3 text-slate-300 leading-relaxed">{tier.rationale}</p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Link
                  to={tier.href}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-slate-950 px-6 py-3 text-sm font-semibold hover:bg-slate-100 transition"
                  data-testid="analyzer-cta-primary"
                  onClick={() => track("TriageCTAClicked", { tier: category.recommendedTier })}
                >
                  {tier.cta}
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/process"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-medium hover:bg-white/5 transition"
                >
                  See our case workflow
                </Link>
              </div>
            </section>

            {/* Evidence checklist */}
            <section className="mb-10 rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8" data-testid="analyzer-checklist">
              <h2 className="text-xl font-semibold mb-2 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-400" />
                Your evidence checklist
              </h2>
              <p className="text-sm text-slate-400 mb-5">
                Whichever path you choose — DIY or with our help — these are the items reviewers will look for in your case.
              </p>
              <ul className="space-y-2.5">
                {checklist.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-300">
                    <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Email capture */}
            <section className="mb-10 rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8" data-testid="analyzer-email-capture">
              {emailSubmitted ? (
                <div className="text-center">
                  <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-3" />
                  <h3 className="text-xl font-semibold mb-2">Result saved.</h3>
                  <p className="text-slate-300 text-sm">
                    We'll email you a copy and follow up if you'd like help with the appeal package.
                  </p>
                </div>
              ) : (
                <form onSubmit={submitEmail} className="space-y-4">
                  <h3 className="text-xl font-semibold flex items-center gap-2">
                    <Mail className="w-5 h-5 text-blue-400" />
                    Email me a copy of this analysis
                  </h3>
                  <p className="text-sm text-slate-400">
                    Optional. We'll send the result above to your inbox so you can reference it later. No marketing emails — just this one analysis and any follow-up if you reach out for help.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="flex-1 px-4 py-3 rounded-xl bg-slate-900 border border-white/15 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                      data-testid="analyzer-email-input"
                      required
                    />
                    <button
                      type="submit"
                      disabled={emailSubmitting}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-500 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-600 transition disabled:opacity-50"
                      data-testid="analyzer-email-submit"
                    >
                      {emailSubmitting ? "Saving..." : "Send me my analysis"}
                    </button>
                  </div>
                  {emailError && (
                    <p className="text-sm text-red-400" data-testid="analyzer-email-error">{emailError}</p>
                  )}
                </form>
              )}
            </section>

            {/* Cross-link to deeper reference */}
            <section className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <h3 className="text-lg font-semibold mb-2">Read further</h3>
              <p className="text-sm text-slate-400 mb-4">
                We've published deep reference articles on the most common categories — sourced directly from each platform's published policy documentation.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <Link
                  to="/blog/instagram-community-guidelines-violations-reference"
                  className="inline-flex items-center justify-between gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm hover:bg-white/10 transition"
                >
                  Instagram CG Categories Reference
                  <ExternalLink className="w-4 h-4 text-slate-500" />
                </Link>
                <Link
                  to="/blog/facebook-business-manager-disabled-complete-reference"
                  className="inline-flex items-center justify-between gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm hover:bg-white/10 transition"
                >
                  Facebook BM Disable Reference
                  <ExternalLink className="w-4 h-4 text-slate-500" />
                </Link>
              </div>
            </section>

            {/* Restart */}
            <div className="mt-12 text-center">
              <button
                type="button"
                onClick={() => {
                  setStep(0);
                  setResult(null);
                  setEmail("");
                  setEmailSubmitted(false);
                }}
                className="text-sm text-slate-400 hover:text-white underline"
                data-testid="analyzer-restart"
              >
                Start a new analysis
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // -----------------------------------------------------------------
  // Questionnaire steps
  // -----------------------------------------------------------------
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Header />
      <main className="py-16 md:py-20">
        <div className="mx-auto max-w-2xl px-6">
          {/* Header */}
          <div className="text-center mb-10" data-testid="analyzer-intro">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/15 border border-blue-500/30 text-blue-300 text-xs font-medium mb-5">
              <Sparkles className="w-3.5 h-3.5" />
              Free · No email required to use
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
              Free Suspension Analyzer
            </h1>
            <p className="mt-4 text-base md:text-lg text-slate-300 leading-relaxed">
              Tell us about your suspension. We'll identify the most likely enforcement category, give you a tailored evidence checklist, and recommend a next step — all in about 60 seconds.
            </p>
          </div>

          {renderProgress()}

          {/* Step 0: Platform */}
          {step === 0 && (
            <section data-testid="analyzer-step-platform">
              <h2 className="text-xl font-semibold mb-2">Which platform suspended your account?</h2>
              <p className="text-sm text-slate-400 mb-6">We currently cover Instagram, Facebook, TikTok, YouTube, and X.</p>
              {renderChoice(PLATFORMS, platform, setPlatform, "analyzer-platform")}
            </section>
          )}

          {/* Step 1: Suspension type */}
          {step === 1 && (
            <section data-testid="analyzer-step-type">
              <h2 className="text-xl font-semibold mb-2">What kind of restriction is it?</h2>
              <p className="text-sm text-slate-400 mb-6">Pick the closest match — we'll refine in the next step.</p>
              {renderChoice(SUSPENSION_TYPES, suspensionType, setSuspensionType, "analyzer-type")}
            </section>
          )}

          {/* Step 2: Account type */}
          {step === 2 && (
            <section data-testid="analyzer-step-account">
              <h2 className="text-xl font-semibold mb-2">What kind of account is it?</h2>
              <p className="text-sm text-slate-400 mb-6">Business and ad-account cases have different appeal paths than personal accounts.</p>
              {renderChoice(ACCOUNT_TYPES, accountType, setAccountType, "analyzer-account")}
            </section>
          )}

          {/* Step 3: Notice + context */}
          {step === 3 && (
            <section data-testid="analyzer-step-notice">
              <h2 className="text-xl font-semibold mb-2">Paste the suspension notice (optional)</h2>
              <p className="text-sm text-slate-400 mb-4">
                The exact language matters. The more we have, the more accurate the diagnosis. We don't store this unless you give us your email at the end.
              </p>
              <textarea
                value={noticeText}
                onChange={(e) => setNoticeText(e.target.value)}
                placeholder={`E.g. "Your account has been disabled for not following our Community Standards on..." — paste the full notice if you have it.`}
                rows={6}
                className="w-full p-4 rounded-xl bg-slate-900 border border-white/15 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 text-sm"
                data-testid="analyzer-notice-textarea"
              />

              <div className="mt-6 space-y-5">
                <div>
                  <label className="block text-sm font-medium mb-2">When did the suspension happen?</label>
                  <select
                    value={daysSince}
                    onChange={(e) => setDaysSince(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-900 border border-white/15 text-white text-sm focus:outline-none focus:border-blue-500"
                    data-testid="analyzer-days-select"
                  >
                    <option value="">Select…</option>
                    <option value="today">Today</option>
                    <option value="this_week">This week</option>
                    <option value="this_month">This month</option>
                    <option value="older">More than a month ago</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Have you already submitted an appeal?</label>
                  <div className="flex gap-3">
                    {[
                      { v: true, l: "Yes" },
                      { v: false, l: "No" },
                    ].map((o) => (
                      <button
                        type="button"
                        key={String(o.v)}
                        onClick={() => setPriorAppeals(o.v)}
                        className={`px-5 py-2 rounded-lg border text-sm ${
                          priorAppeals === o.v
                            ? "border-blue-500 bg-blue-500/15 text-white"
                            : "border-white/15 bg-white/5 text-slate-300 hover:bg-white/10"
                        }`}
                        data-testid={`analyzer-priorappeal-${o.v ? "yes" : "no"}`}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Is revenue impacted (lost sales, paused ads, etc.)?</label>
                  <div className="flex gap-3">
                    {[
                      { v: true, l: "Yes" },
                      { v: false, l: "No" },
                    ].map((o) => (
                      <button
                        type="button"
                        key={String(o.v)}
                        onClick={() => setRevenueImpacted(o.v)}
                        className={`px-5 py-2 rounded-lg border text-sm ${
                          revenueImpacted === o.v
                            ? "border-blue-500 bg-blue-500/15 text-white"
                            : "border-white/15 bg-white/5 text-slate-300 hover:bg-white/10"
                        }`}
                        data-testid={`analyzer-revenue-${o.v ? "yes" : "no"}`}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Nav */}
          <div className="mt-10 flex items-center justify-between">
            {step > 0 ? (
              <button
                type="button"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white"
                data-testid="analyzer-back"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            ) : (
              <span />
            )}

            <button
              type="button"
              disabled={!canAdvance}
              onClick={() => {
                if (step < TOTAL_STEPS - 1) {
                  track("TriageStepCompleted", { step });
                  setStep((s) => s + 1);
                } else {
                  runAnalysis();
                }
              }}
              className="inline-flex items-center gap-2 rounded-full bg-white text-slate-950 px-6 py-3 text-sm font-semibold hover:bg-slate-100 transition disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="analyzer-next"
            >
              {step === TOTAL_STEPS - 1 ? "Analyze My Suspension" : "Continue"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

// Used by sitemap + tests
export const ANALYZER_CATEGORIES = CATEGORIES;
